import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import "./App.css";
import DriverDetailPage from "./pages/inventory/DriverDetailPage";
import {
  OrderOutlet,
  WorkspacePage,
  UserPage,
  CompaniesPage,
  CompanyDetailPage,
  WorkspaceUserPage,
  DriversPage,
  VehiclesPage,
  Customers,
  ProductsPage,
  CreateOrder,
  CreateOrderRoute,
  EditOrder,
  OrderTemplatesPage,
  EditOrderTemplate,
  TemplateAdminRoute,
  AllOrdersPage,
  Invoices,
  Locations,
  RealtimeDashboardPage,
  Reports,
  PlannerPage,
  PublicOrderPage,
  PublicOrderTrackingPage,
  SettingsPage,
  IntegrationDetailPage,
  IncomingOrders,
  AdminDashboardPage,
  OnboardingPage,
} from "./pages";
import {
  LoginPage,
  LogoutPage,
  SignUpPage,
  PasswordResetPage,
  WorkspaceLoader,
} from "./pages/auth";
import { NotFoundPage, ServerErrorPage, MaintenancePage } from "./pages/utils";
import { useDarkMode } from "./context/DarkModeContext";
import { useUserSession } from "./context/UserContext";
import type { FC, PropsWithChildren } from "react";
import { lazy, Suspense } from "react";
import CouponPage from "./pages/CouponPage";
import OAuthAuthorizePage from "./pages/OAuthAuthorizePage";
import AdminOAuthAuthorizePage from "./pages/AdminOAuthAuthorizePage";
import ShopifyAppHomePage from "./pages/ShopifyAppHomePage";
import ShopifyWorkspaceLinkPage from "./pages/ShopifyWorkspaceLinkPage";
import { DriverMapPage } from "./pages/orders/Map/DriverMap";
import { layoutPage, panelSm } from "@design-system";
import { I18nextProvider } from "react-i18next";
import i18nPublic from "./i18nPublic";

const DailyOverviewPage = lazy(() =>
  import("./pages/administration/DailyOverview").then((module) => ({
    default: module.DailyOverviewPage,
  })),
);

// Ny komponent for global lastingsvisning
const GlobalLoadingFallback = () => {
  return (
    <div className={`min-h-screen ${layoutPage}`}>
      {/* Navbar mockup */}
      <div
        className={`fixed top-0 left-0 right-0 h-[85px] bg-surface border-b border-border z-50 flex items-center justify-between px-4`}
      >
        {/* Logo */}
        <div className="h-8 bg-surface-muted w-32 rounded-md animate-pulse"></div>

        {/* Høyre side av navbar */}
        <div className="flex items-center space-x-4">
          <div className="h-8 w-8 bg-surface-muted rounded-full animate-pulse"></div>
          <div className="h-8 w-8 bg-surface-muted rounded-full animate-pulse"></div>
          <div className="h-8 w-8 bg-surface-muted rounded-full animate-pulse"></div>
        </div>
      </div>

      {/* Sidebar mockup */}
      <div
        className={`fixed top-[85px] left-0 bottom-0 w-64 bg-surface border-r border-border hidden lg:block`}
      >
        {/* Sidebar menu items */}
        <div className="p-4 space-y-4">
          {[...Array(8)].map((_, index) => (
            <div key={index} className="flex items-center space-x-3">
              <div className="h-6 w-6 bg-surface-muted rounded animate-pulse"></div>
              <div className="h-4 bg-surface-muted rounded w-32 animate-pulse"></div>
            </div>
          ))}
        </div>
      </div>

      {/* Main content area */}
      <div className="pt-[85px] lg:ml-64 min-h-screen">
        <div className="px-4 sm:px-8 pt-6 sm:pt-12 flex justify-center">
          <div className="w-full max-w-[1370px]">
            {/* Header mockup */}
            <div className="flex justify-between items-center mb-8">
              <div>
                <div className="h-8 bg-surface-muted w-48 rounded-md animate-pulse mb-2"></div>
                <div className="h-4 bg-surface-muted w-64 rounded-md animate-pulse"></div>
              </div>
              <div className="h-10 bg-surface-muted w-32 rounded-md animate-pulse"></div>
            </div>

            {/* Content mockup */}
            <div className="space-y-4">
              {/* Search bar mockup */}
              <div className="h-12 bg-surface-muted w-full max-w-md rounded-md animate-pulse mb-6"></div>

              {/* Table mockup */}
              <div className={`${panelSm} overflow-hidden`}>
                {/* Table header */}
                <div className="h-12 bg-gray-100 dark:bg-gray-800 flex items-center px-4">
                  {[...Array(3)].map((_, index) => (
                    <div
                      key={index}
                      className="h-4 bg-surface-muted w-32 rounded-md animate-pulse mr-8"
                    ></div>
                  ))}
                </div>

                {/* Table rows */}
                {[...Array(5)].map((_, index) => (
                  <div
                    key={index}
                    className="h-16 border-t border-border flex items-center px-4"
                  >
                    {[...Array(3)].map((_, colIndex) => (
                      <div
                        key={colIndex}
                        className="h-4 bg-surface-muted w-32 rounded-md animate-pulse mr-8"
                      ></div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Guards admin-only pages. The sidebar hides these links for non-admins, but
// the routes were still reachable via direct/deep link and would render an
// empty/stuck page once Firestore denied the data. Redirect non-admins.
const AdminRoute: FC<PropsWithChildren> = ({ children }) => {
  const { userSession } = useUserSession();
  if (!userSession) return <Navigate to="/login" replace />;
  if (!userSession.user.isAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
};

const LegacyDailyPlannerRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/daily-planner${search}`} replace />;
};

function App() {
  const { isDarkMode } = useDarkMode();

  if (isDarkMode) {
    document.getElementById("root")?.classList.add("dark");
    document
      .getElementsByTagName("body")[0]
      .classList.remove("light-scrollbar");
    document.getElementsByTagName("body")[0].classList.add("dark-scrollbar");
  } else {
    document.getElementById("root")?.classList.remove("dark");
    document.getElementsByTagName("body")[0].classList.remove("dark-scrollbar");
    document.getElementsByTagName("body")[0].classList.add("light-scrollbar");
  }

  return (
    <Suspense fallback={<GlobalLoadingFallback />}>
      <Routes>
        <Route path="/track" element={<PublicOrderTrackingPage />} />
        <Route
          path="/order/:identifier"
          element={
            <I18nextProvider i18n={i18nPublic}>
              <PublicOrderPage />
            </I18nextProvider>
          }
        />
        <Route path="/login" element={<LoginPage />}></Route>
        <Route path="/logout" element={<LogoutPage />}></Route>
        <Route path="/signup" element={<SignUpPage />}></Route>
        <Route path="/shopify/app" element={<ShopifyAppHomePage />}></Route>
        <Route
          path="/shopify/connect"
          element={<ShopifyWorkspaceLinkPage />}
        ></Route>
        <Route path="/oauth/authorize" element={<OAuthAuthorizePage />}></Route>
        <Route
          path="/admin-mcp/oauth/authorize"
          element={<AdminOAuthAuthorizePage />}
        ></Route>
        <Route path="/load_workspace" element={<WorkspaceLoader />}></Route>
        <Route path="/reset_password" element={<PasswordResetPage />}></Route>
        <Route path="/onboarding" element={<OnboardingPage />}></Route>
        <Route path="/" element={<RealtimeDashboardPage />} />
        <Route path="/dashboard" element={<RealtimeDashboardPage />} />
        <Route path="/administration">
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route
            path="dashboard"
            element={
              <AdminRoute>
                <AdminDashboardPage />
              </AdminRoute>
            }
          />
          <Route
            path="users"
            element={
              <AdminRoute>
                <UserPage />
              </AdminRoute>
            }
          />
          <Route
            path="workspaces"
            element={
              <AdminRoute>
                <WorkspacePage />
              </AdminRoute>
            }
          />
          <Route
            path="daily-overview"
            element={<LegacyDailyPlannerRedirect />}
          />
          <Route path="reports" element={<Reports />} />
        </Route>
        <Route path="/orders" element={<OrderOutlet />}>
          <Route index element={<AllOrdersPage />} />
          <Route path="new" element={<CreateOrderRoute />} />
          <Route path="edit/:id" element={<EditOrder />} />
          <Route path="scheduled" element={<Navigate to="/orders" replace />} />
          <Route path="ongoing" element={<Navigate to="/orders" replace />} />
          <Route path="completed" element={<Navigate to="/orders" replace />} />
          <Route path="all" element={<Navigate to="/orders" replace />} />
          <Route path="templates" element={<Navigate to="/order-templates" replace />} />
        </Route>
        <Route path="/order-templates" element={<OrderOutlet />}>
          <Route index element={<OrderTemplatesPage />} />
          <Route
            path="new"
            element={
              <TemplateAdminRoute>
                <CreateOrder mode="template" />
              </TemplateAdminRoute>
            }
          />
          <Route
            path="edit/:id"
            element={
              <TemplateAdminRoute>
                <EditOrderTemplate />
              </TemplateAdminRoute>
            }
          />
        </Route>
        <Route path="/incoming-orders" element={<IncomingOrders />} />
        <Route path="/planner" element={<PlannerPage />} />
        <Route path="/daily-planner" element={<DailyOverviewPage />} />
        <Route path="/invoices" element={<Invoices />} />
        <Route path="map" element={<DriverMapPage />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/resources">
          <Route path="drivers" element={<DriversPage />} />
          <Route path="drivers/:driverId" element={<DriverDetailPage />} />
          <Route path="vehicles" element={<VehiclesPage />} />
          <Route path="products" element={<ProductsPage />} />
          <Route path="locations" element={<Locations />} />
        </Route>

        <Route path="/workspace">
          <Route path="users" element={<WorkspaceUserPage />} />
          <Route path="companies" element={<CompaniesPage />} />
          <Route path="companies/:companyId" element={<CompanyDetailPage />} />
          <Route path="coupons" element={<CouponPage />} />
        </Route>
        <Route path="/settings" element={<SettingsPage />} />
        <Route
          path="/settings/integrations/:integrationKey"
          element={<IntegrationDetailPage />}
        />
        <Route path="/maintenance" element={<MaintenancePage />} />
        <Route path="/404" element={<NotFoundPage />} />
        <Route path="/500" element={<ServerErrorPage />} />
        <Route path="/reports" element={<Reports />} />
      </Routes>
    </Suspense>
  );
}

export default App;
